import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { Typography } from '@interstate/components/Typography';
import type { SelectInputEventValue, SelectInputOption } from '@interstate/components/SelectInput';

import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';

import { capitalize } from 'lodash';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { useSingleTradeInFormProvider } from '../MultiTradeIn/TradeInFormProvider';

// components
import { SelectInput } from './TradeInSelectInput';

// consts
import { TRADE_IN_EXTERIOR_COLOR } from '@makemydeal/dr-dash-components';
import { EXTERIOR_COLORS, ZERO_WIDTH_SPACE } from '../../../constants';

// styling
import { DetailContainer } from '../trade.interstate.style';

interface ExteriorColorFieldProps {
    hasTradeIn: boolean;
    isManualFormOpen: boolean;
    disabled?: boolean;
}

export const ExteriorColorField: React.FC<ExteriorColorFieldProps> = ({ hasTradeIn, isManualFormOpen, disabled }) => {
    const dispatch = useDispatch();
    const { tradeInIndex } = useSingleTradeInFormProvider();
    const vehicleExteriorColor = capitalize(useSelector(tradeInsSelectors.getExteriorColor(tradeInIndex)));
    const tradeInVehicle = useSelector(tradeInsSelectors.getTradeInComponentVehicle(tradeInIndex));

    const getVehicleExteriorColorOptions = useMemo(
        (): SelectInputOption[] => EXTERIOR_COLORS.map((color) => ({ label: color, value: color })).sort(),
        []
    );

    const onExteriorChange = (event: InterstateOnChangeEvent<SelectInputEventValue>) => {
        const color = event.target.value as string;
        dispatch(tradeInsActionCreators.updateTradeInExteriorColor(color, tradeInIndex));
        dispatch(tradeInsActionCreators.updateVehicle({ ...tradeInVehicle, color }, tradeInIndex));
    };

    return (
        <>
            {isManualFormOpen ? (
                <SelectInput
                    data-testid="trade-exterior-color-select-input"
                    label={TRADE_IN_EXTERIOR_COLOR}
                    name={TRADE_IN_EXTERIOR_COLOR}
                    onChange={onExteriorChange}
                    options={getVehicleExteriorColorOptions}
                    value={vehicleExteriorColor}
                    placeholder="Select"
                    displayDeselectOption={true}
                    disabled={disabled}
                    size="sm"
                />
            ) : (
                <>
                    {hasTradeIn && (
                        <DetailContainer>
                            <Typography variant="label-md" color="sem.color.on-surface.muted" sx={{ marginBottom: '8px' }}>
                                {TRADE_IN_EXTERIOR_COLOR}
                            </Typography>
                            <Typography variant="body-md" tag="span" data-testid="trade-exterior-color-value-label">
                                {vehicleExteriorColor || ZERO_WIDTH_SPACE}
                            </Typography>
                        </DetailContainer>
                    )}
                </>
            )}
        </>
    );
};

export default ExteriorColorField;
